/* ---------- src/pages/Home.js ---------- */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../App.css";

/* ---------- Image Arrays ---------- */
const heroImages = [
  "/images/slide1.jpg",
  "/images/slide2.jpg",
  "/images/slide3.jpg",
  "/images/slide4.jpg",
  "/images/slide5.jpg",
  "/images/slide6.jpg",
];

const galleryImages = [
  "/images/photo1.jpg",
  "/images/photo2.jpg",
  "/images/photo3.jpg",
  "/images/photo4.jpg",
];

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  /* Taglines come from i18n so they re-translate live */
  const taglines = [t("homeTagline1"), t("homeTagline2"), t("homeTagline3")];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTagline, setCurrentTagline] = useState(0);
  const [showScroll, setShowScroll] = useState(false);

  /* ------------ Slideshow + Tagline rotation ------------ */
  useEffect(() => {
    const slideTimer = setInterval(
      () => setCurrentSlide((p) => (p + 1) % heroImages.length),
      4000
    );
    const tagTimer = setInterval(
      () => setCurrentTagline((p) => (p + 1) % taglines.length),
      3000
    );
    const onScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);

    return () => {
      clearInterval(slideTimer);
      clearInterval(tagTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [taglines.length]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div
      className={isRTL ? "arabic-font" : ""}
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      {/* -------- HERO -------- */}
      <div style={{ position: "relative", height: "48vh", overflow: "hidden" }}>
        <img
          src={heroImages[currentSlide]}
          alt={`Hero ${currentSlide + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "opacity 1s",
          }}
        />

        {/* Tagline + CTA */}
        <div
          style={{
            position: "absolute",
            bottom: "2rem",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(255,244,236,0.9)",
            padding: "1rem 2rem",
            borderRadius: "12px",
            textAlign: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <h2
            className="storybook-heading"
            style={{ color: "#F4888C", marginBottom: "0.5rem" }}
          >
            {taglines[currentTagline]}
          </h2>

          <div className="button-group">
            <a href="/books" className="btn">
              {t("homeExploreBtn")}
            </a>
            <a href="/founder" className="btn">
              {t("homeFounderBtn")}
            </a>
            <a href="/student-space" className="btn">
              {t("homeStudentSpaceBtn")}
            </a>
          </div>
        </div>
      </div>

      {/* -------- WELCOME MESSAGE -------- */}
      <section
        style={{
          background: "#FFF4EC",
          padding: "3rem 1.5rem",
          textAlign: "center",
          borderTop: "8px solid #F4888C",
          borderBottom: "4px solid #F4888C",
        }}
      >
        <h1
          style={{
            fontSize: "2.5rem",
            color: "#F4888C",
            marginBottom: "1rem",
          }}
        >
          {t("homeWelcomeTitle")}
        </h1>
        <p
          style={{
            fontSize: "1.2rem",
            maxWidth: 800,
            margin: "0 auto",
            color: "#4b5563",
            lineHeight: 1.8,
          }}
        >
          {t("homeWelcomeText")}
        </p>
      </section>

      {/* -------- GALLERY -------- */}
      <section className="container">
        <h2 style={{ textAlign: "center" }}>{t("homeGalleryTitle")}</h2>
        <div className="gallery-horizontal">
          {galleryImages.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Moment ${i + 1}`}
              className="gallery-img"
            />
          ))}
        </div>
      </section>

      {/* -------- TILE IMAGE -------- */}
      <section style={{ textAlign: "center", margin: "4rem 0" }}>
        <img
          src="/images/tile.jpg"
          alt={t("homeTileAlt")}
          style={{
            width: "100%",
            maxWidth: 600,
            borderRadius: 20,
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          }}
        />
      </section>

      {/* -------- YOUTUBE -------- */}
      <section
        className="container"
        style={{ textAlign: "center", marginTop: "4rem" }}
      >
        <h2 style={{ color: "#F4888C" }}>{t("homeWatchTitle")}</h2>
        <p style={{ maxWidth: 700, margin: "0 auto 2rem", color: "#4b5563" }}>
          {t("homeWatchDesc")}
        </p>

        <div
          style={{
            position: "relative",
            paddingBottom: "56.25%",
            height: 0,
            overflow: "hidden",
          }}
        >
          <iframe
            title="MyDearTeacher YouTube"
            src="https://www.youtube.com/embed/Qj4K0vSKrUM"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              borderRadius: 16,
            }}
          ></iframe>
        </div>
      </section>

      {/* -------- WHATSAPP FLOAT -------- */}
      <div
        style={{ position: "fixed", bottom: "2rem", right: "2rem", zIndex: 999 }}
      >
        <a
          href="https://wa.me/971505816347"
          target="_blank"
          rel="noreferrer"
          title="WhatsApp"
        >
          <img
            src="/images/whatsapp-icon.png"
            alt="WhatsApp"
            style={{ width: 60, height: 60, transition: "transform .3s" }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
          />
        </a>
      </div>

      {/* -------- SCROLL-TOP BUTTON -------- */}
      {showScroll && (
        <button
          className="scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default Home;
